import { useState, useEffect } from "react";
import type { Dispatch, SetStateAction } from "react";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import {
  useFeatureFlags,
  useIsAdmin,
  useOutboundCallRecordingSetting,
} from "../../reducers/layoutReducer/selectors";

type OutboundRecordingValue = "automatic" | "manual" | "disabled" | null | undefined;
type OutboundNotificationMessageCondition = "always" | "check" | "never" | null | undefined;

interface OutboundCallRecordingSetting {
  outboundRecording: OutboundRecordingValue;
  outboundNotificationMessage: boolean;
  outboundNotificationMessageCondition: OutboundNotificationMessageCondition;
}

const OUTBOUND_RECORDING_VALUES = ["automatic", "manual", "disabled"] as const;
const OUTBOUND_NOTIFICATION_CONDITIONS = ["always", "check", "never"] as const;

const toOutboundCallRecordingSetting = (value: unknown): OutboundCallRecordingSetting => {
  const settings = value && typeof value === "object" ? (value as Record<string, unknown>) : {};

  const outboundRecording = OUTBOUND_RECORDING_VALUES.includes(
    settings.outboundRecording as (typeof OUTBOUND_RECORDING_VALUES)[number],
  )
    ? (settings.outboundRecording as (typeof OUTBOUND_RECORDING_VALUES)[number])
    : null;

  const outboundNotificationMessageCondition = OUTBOUND_NOTIFICATION_CONDITIONS.includes(
    settings.outboundNotificationMessageCondition as (typeof OUTBOUND_NOTIFICATION_CONDITIONS)[number],
  )
    ? (settings.outboundNotificationMessageCondition as (typeof OUTBOUND_NOTIFICATION_CONDITIONS)[number])
    : null;

  return {
    outboundRecording,
    outboundNotificationMessage: Boolean(settings.outboundNotificationMessage),
    outboundNotificationMessageCondition,
  };
};

interface FeatureFlags {
  callRecording: boolean;
  callRecordingAdjustments: boolean;
  dialerLeadSummary: boolean;
  dialerImprovementUpdates?: boolean;
}

interface ValidLeadCounts {
  totalLeads: number;
  validLeads: number;
  hasOptedOutPhoneLeads: boolean;
}

interface ConferenceConfirmationState {
  consentChecked: boolean;
  featureFlags: FeatureFlags;
  isAdmin: boolean;
  loadingValidLeads: boolean;
  outboundNotificationMessage: boolean;
  outboundNotificationMessageCondition: OutboundNotificationMessageCondition;
  outboundRecording: OutboundRecordingValue;
  outboundRecordingEnabled: boolean;
  setConsentChecked: Dispatch<SetStateAction<boolean>>;
  startDisabled: boolean;
  validLeadCounts: ValidLeadCounts;
}

const useConferenceConfirmation = ({
  selectedLeadUuids,
  show,
}: {
  selectedLeadUuids: string[];
  show: boolean;
}): ConferenceConfirmationState => {
  const featureFlags = useFeatureFlags() as FeatureFlags;
  const isAdmin = useIsAdmin() as boolean;
  const outboundCallRecordingSetting = toOutboundCallRecordingSetting(useOutboundCallRecordingSetting());
  const { outboundRecording, outboundNotificationMessage, outboundNotificationMessageCondition } =
    outboundCallRecordingSetting;

  const [consentChecked, setConsentChecked] = useState(!featureFlags.callRecordingAdjustments);
  const [validLeadCounts, setValidLeadCounts] = useState<ValidLeadCounts>({
    totalLeads: 0,
    validLeads: 0,
    hasOptedOutPhoneLeads: false,
  });
  const [loadingValidLeads, setLoadingValidLeads] = useState(true);

  const outboundRecordingEnabled = featureFlags.callRecording && outboundRecording !== "disabled";

  const checkValidLeads = () => {
    setLoadingValidLeads(true);
    caseTransformingAxios
      .post<{ totalLeads: number; validLeads: number; hasOptedOutPhoneLeads: boolean }>(
        "/dialer/check_valid_leads",
        { leadUuids: selectedLeadUuids },
      )
      .then((resp) => {
        setValidLeadCounts(resp.data);
        setLoadingValidLeads(false);
      })
      .catch(() => {
        setValidLeadCounts({
          totalLeads: 0,
          validLeads: 0,
          hasOptedOutPhoneLeads: false,
        });
        setLoadingValidLeads(false);
      });
  };

  useEffect(() => {
    if (show) {
      checkValidLeads();
      setConsentChecked(!featureFlags.callRecordingAdjustments);
    }
  }, [show]);

  const startDisabled =
    validLeadCounts.validLeads === 0 ||
    (outboundRecordingEnabled && outboundNotificationMessageCondition !== "always" && !consentChecked);

  return {
    consentChecked,
    featureFlags,
    isAdmin,
    loadingValidLeads,
    outboundNotificationMessage,
    outboundNotificationMessageCondition,
    outboundRecording,
    outboundRecordingEnabled,
    setConsentChecked,
    startDisabled,
    validLeadCounts,
  };
};

export default useConferenceConfirmation;
